import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { listRequests, getRequestCounts, subscribeRequests } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/**
 * Requests inbox state (CT-4200): status filter + search over the shared requests
 * seed, plus per-status counts. `reload()` re-fetches so detail-panel mutations
 * (CT-4201) reflect immediately. Race/unmount-safe.
 *
 * `requesterId` (CT-4203): when set (end-user "My requests" view), the list is
 * scoped to that owner; omit it for the tenant-wide admin inbox.
 */
export function useRequestsInbox({ requesterId } = {}) {
  const { t } = useTranslation('admin-studio');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ total: 0, waiting_on_you: 0, by_status: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      // Counts are the tenant-wide inbox header stat; the end-user "My requests"
      // view (requesterId set) never renders them, so skip that fetch there.
      const [listRes, countsRes] = await Promise.all([
        listRequests({ status, search, requester_id: requesterId }),
        requesterId ? Promise.resolve(null) : getRequestCounts(),
      ]);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setEntries(validateApiResponse(listRes, t('common.requestFailed')).body ?? []);
      if (countsRes) setCounts((prev) => validateApiResponse(countsRes, t('common.requestFailed')).body ?? prev);
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, requesterId, t]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  // Re-fetch when the shared requests store changes (CT-4205 UX): a request filed
  // from the modal — even while this inbox is already mounted — shows up without
  // the user navigating away and back. Also keeps the list in sync with detail-
  // panel mutations from anywhere.
  useEffect(() => subscribeRequests(reload), [reload]);

  return useMemo(
    () => ({ entries, counts, status, setStatus, search, setSearch, isLoading, error, reload }),
    [entries, counts, status, search, isLoading, error, reload],
  );
}
